// File: src/components/AIGuideDialog.tsx
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Chip,
    Divider,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {Close as CloseIcon, ExpandMore as ExpandMoreIcon} from '@mui/icons-material';
import {AIService} from '../types';

interface AIGuideDialogProps {
    open: boolean;
    onClose: () => void;
    aiService: AIService | null;
}

const AIGuideDialog: React.FC<AIGuideDialogProps> = ({open, onClose, aiService}) => {
    const formatJson = (data: any) => {
        if (!data || Object.keys(data).length === 0) {
            return '{}';
        }
        return JSON.stringify(data, null, 2);
    };

    // ✅ ИСПРАВЛЕНО: Включаем task_specific_settings
    const combinedConfig = aiService ? {
        system_config: aiService.system_config,
        default_settings: aiService.default_settings,
        task_specific_settings: aiService.task_specific_settings, // ← ДОБАВЛЕНО
        pricing_config: aiService.pricing_config
    } : {};

    return (
        <Dialog onClose={onClose} open={open} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        AI Service Details
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon/>
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {aiService ? (
                    <>
                        <Typography variant="h5" component="h2" gutterBottom>
                            {aiService.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
                            Code: **{aiService.code}**
                        </Typography>

                        <Divider sx={{my: 2}}/>

                        <Typography variant="subtitle1" gutterBottom>
                            **Description:**
                        </Typography>
                        <Typography variant="body2" sx={{mb: 2}}>
                            {aiService.description || 'No description available.'}
                        </Typography>

                        <Typography variant="subtitle1" gutterBottom>
                            **Supported Tasks:**
                        </Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2}}>
                            {aiService.supported_tasks?.length > 0 ? (
                                aiService.supported_tasks.map((task: string, index: number) => (
                                    <Chip key={index} label={task} variant="outlined" size="small"/>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No tasks listed.
                                </Typography>
                            )}
                        </Box>

                        {/* ✅ УЛУЧШЕНО: Добавлена детализация task-specific настроек */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                <Typography variant="h6">⚙️ Task-Specific Settings</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{display: 'grid', gap: 2}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Оптимизированные настройки для разных типов задач.
                                    </Typography>

                                    {aiService.task_specific_settings && Object.keys(aiService.task_specific_settings).length > 0 ? (
                                        Object.entries(aiService.task_specific_settings).map(([taskType, settings]) => (
                                            <Box key={taskType} sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                                    📋 {taskType}
                                                </Typography>
                                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                                                    <pre style={{
                                                        margin: 0,
                                                        fontSize: '13px',
                                                        whiteSpace: 'pre-wrap',
                                                        wordWrap: 'break-word',
                                                        fontFamily: 'Monaco, "Courier New", monospace'
                                                    }}>
                                                        {formatJson(settings)}
                                                    </pre>
                                                </Paper>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Нет специфических настроек для задач.
                                        </Typography>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                <Typography variant="h6">🔧 Full Configuration</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{display: 'grid', gap: 2}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Полная конфигурация сервиса (read-only).
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            bgcolor: 'rgba(30, 30, 30, 0.95)',
                                            overflow: 'auto',
                                            color: 'text.primary',
                                            maxHeight: '400px'
                                        }}
                                    >
                                        <pre style={{
                                            margin: 0,
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word',
                                            fontSize: '12px'
                                        }}>
                                            {formatJson(combinedConfig)}
                                        </pre>
                                    </Paper>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        Select an AI service to view details.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AIGuideDialog;
